import React, { useMemo, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Formik, Form, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { selectCountryList, selectIndustryList, selectBusinessTypesOptions } from 'state/constants'
import RectangleButton from 'particles/Button/RectangleButton'
import Inputs from 'components/Inputs'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import styled from 'particles/themes/styled'
import Modal from 'components/Team/common/Modals'
import RichInfoList from 'particles/TextInputs/TextList/RichInfoList'
import { getStringLength } from 'components/utils'
import * as yup from 'yup'
import { TFunction } from 'i18next'
import { RegisterActions } from 'services/api/register'
import { selectAuthErrors, errorMessages, resetAuthErrors } from 'state/error/authErrors'
import { selectProfileDetails, selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { COUNTRY_ENUM } from 'services/api/constants'

import _ from 'lodash'

const FormContainer = styled(Form)`
    width: 100%;
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[600]};
    .buttons-group {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};
    }
`
interface BasicFormProps<T> {
    value: T
    onSubmit: (value: T) => void
}

export const AccountCreationForm: React.FC<BasicFormProps<string>> = ({ value, onSubmit }) => {
    const [t] = useTranslation()

    const accountCreationValidation = useMemo(
        () =>
            yup.object({
                username: yup.string().required(t('errorMessage.signUp.enterName')),
            }),
        [t],
    )

    return (
        <Formik
            initialValues={{
                username: value || '',
            }}
            enableReinitialize
            validateOnBlur={true}
            validateOnChange={false}
            validationSchema={accountCreationValidation}
            onSubmit={values => {
                onSubmit(values.username)
            }}
        >
            {({ handleBlur }) => {
                return (
                    <FormContainer>
                        <Inputs.TextField
                            type="text"
                            name="username"
                            onBlur={handleBlur}
                            placeholder={t('logIn.signUp.namePlaceHolder')}
                        />
                        <RectangleButton type="submit" label={t('button.next')} size="l" />
                    </FormContainer>
                )
            }}
        </Formik>
    )
}

const countryLabels = (t: TFunction) => {
    return {
        [COUNTRY_ENUM.SG]: t('country.singapore'),

        [COUNTRY_ENUM.ID]: t('country.indonesia'),

        [COUNTRY_ENUM.TW]: t('country.taiwan'),

        [COUNTRY_ENUM.PH]: t('country.philippines'),

        [COUNTRY_ENUM.MY]: t('country.malaysia'),

        [COUNTRY_ENUM.HK]: t('country.hongKong'),

        [COUNTRY_ENUM.TH]: t('country.thailand'),

        [COUNTRY_ENUM.VN]: t('country.vietnam'),
    }
}

const phoneNumberPattern = /[^\d+]/g

export interface BusinessDetailValues {
    business_type: null | string
    company_name: null | string
    industry: null | string
    industry_others: null | string
    country: null | string
    business_link: null | string
    business_contact_number?: string
}

export const BusinessDetailForm: React.FC<BasicFormProps<BusinessDetailValues>> = ({ value, onSubmit }) => {
    const [t] = useTranslation()
    const businessTypeOptions = useSelector(selectBusinessTypesOptions)
    const countries = useSelector(selectCountryList)
    const industryOptions = useSelector(selectIndustryList)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.SignUpDetails]))

    const businessDetailValidation = useMemo(
        () =>
            yup.object({
                business_type: yup.string().nullable().required(t('errorMessage.signUp.selectBussinessType')),
                company_name: yup.string().nullable().required(t('errorMessage.signUp.enterCompanyName')),
                industry: yup.string().nullable().required(t('errorMessage.signUp.selectIndustry')),
                country: yup.string().nullable().required(t('errorMessage.signUp.countryToLaunchCampaign')),
                business_contact_number: yup
                    .string()
                    .nullable()
                    .required(t('logIn.signUp.contactNumberErrorMessage.empty')),
                industry_others: yup
                    .string()
                    .nullable()
                    .test({
                        message: () => t('errorMessage.validation.enterIndustry'),
                        test: function (value) {
                            const isOtherIndustry = this.parent.industry === 'others'
                            if (isOtherIndustry) {
                                return Boolean(value)
                            } else {
                                return true
                            }
                        },
                    })
                    .test({
                        message: () => t('errorMessage.validation.enterIndustryCharacterLimit'),
                        test: function (value) {
                            const isOtherIndustry = this.parent.industry === 'others'
                            if (isOtherIndustry) {
                                let charLength = 0
                                if (value) {
                                    charLength = getStringLength(value)
                                }
                                return charLength > 0 && charLength <= 50
                            } else {
                                return true
                            }
                        },
                    }),
                business_link: yup
                    .string()
                    .nullable()
                    .test({
                        message: () => t('campaign.template.downloadAppFrom.errorMessage.wrongFormate'),
                        test: value => {
                            const linkPattern = /((https|http)?:\/\/[^\s]+\.[\w]{2,3})/g
                            if (value) {
                                return linkPattern.test(value)
                            }

                            return true
                        },
                    })
                    .required(t('team.billingInformation.businessDigitalPresence.emptyError')),
            }),
        [t],
    )
    const countryIds = useMemo(() => {
        const labels = countryLabels(t)
        return Object.values(
            countries.map(country => {
                return {
                    value: country.id,
                    label: labels[country.id as COUNTRY_ENUM],
                }
            }),
        )
    }, [countries, t])

    return (
        <Formik
            initialValues={{
                business_type: value.business_type,
                company_name: value.company_name,
                industry: value.industry,
                industry_others: value.industry_others,
                country: value.country,
                business_link: value.business_link,
                business_contact_number: value.business_contact_number,
            }}
            enableReinitialize
            validateOnBlur={true}
            validationSchema={businessDetailValidation}
            onSubmit={values => {
                if (!isLoading) {
                    onSubmit(values)
                }
            }}
        >
            {({ handleBlur }) => {
                return (
                    <FormContainer>
                        <FormikRadioButtonGroup
                            name="business_type"
                            title={t('logIn.signUp.businessTypeTitle')}
                            isRequired
                            options={businessTypeOptions}
                            orientation="horizontal"
                            labelSize="l"
                        />
                        <Inputs.TextField
                            type="text"
                            name="company_name"
                            onBlur={handleBlur}
                            label={t('logIn.signUp.companyNameTitle')}
                            labelSize="l"
                            placeholder={t('logIn.signUp.companyNamePlaceholder')}
                            isRequired
                        />
                        <Modal.DropDownSelector
                            isDisabled={!industryOptions}
                            options={industryOptions}
                            placeholder={t('placeholderText.selectIndustry')}
                            title={t('logIn.signUp.industryTitle')}
                            name="industry"
                            customInputCondition={values => values?.industry === 'others'}
                            customInputName="industry_others"
                            isRequired
                            isSearchable
                            dataTestId="test-industry-selector"
                            labelSize="l"
                        />
                        <Modal.DropDownSelector
                            isDisabled={!countryIds}
                            options={countryIds}
                            placeholder={t('placeholderText.selectCountry')}
                            title={t('logIn.signUp.countryToLaunchTitle')}
                            name="country"
                            dataTestId="test-country-selector"
                            labelSize="l"
                            isRequired
                        />
                        <Inputs.TextField
                            type="text"
                            name="business_contact_number"
                            onBlur={handleBlur}
                            matchWith={phoneNumberPattern}
                            label={t('logIn.signUp.contactNumberTitle')}
                            subtext={t('logIn.signUp.contactNumberSubtext')}
                            labelSize="l"
                            placeholder={t('logIn.signUp.contactIDNumberPlaceHolder')}
                            isRequired
                        />
                        <Inputs.TextField
                            type="text"
                            name="business_link"
                            onBlur={handleBlur}
                            label={t('logIn.signUp.digitalPresence.title')}
                            labelSize="l"
                            placeholder={t('placeholderText.websiteExample')}
                            isRequired
                        />
                        <RectangleButton
                            type="submit"
                            label={t('button.next')}
                            size="l"
                            isLoading={isLoading}
                            isDisabled={isLoading}
                        />
                    </FormContainer>
                )
            }}
        </Formik>
    )
}

interface Member {
    id: string
    value: { email: string; name: string }
}

const ErrorBoundary: React.FC = ({ children }) => {
    const [t] = useTranslation()
    const { values, setErrors } = useFormikContext<any>()
    const errors = useSelector(selectAuthErrors)
    const previousValues = useRef(null)
    const messages = useMemo(() => errorMessages(t), [t])
    const dispatch = useDispatch()

    useEffect(() => {
        const members = values?.members

        if (errors) {
            if (!_.isEqual(previousValues.current, values)) {
                dispatch(resetAuthErrors())
            } else {
                const customizeErrors: { members: { value: string }[] } = { members: [] }
                Object.keys(errors).forEach((errorKey: string) => {
                    const index = members.findIndex((member: Member) => member.value.email === errorKey)
                    customizeErrors.members[index] = {
                        value: messages[errors[errorKey]] || t('errorMessage.general.somethingWentWrongTitle'),
                    }
                })
                if (customizeErrors.members.length > 0) {
                    setErrors(customizeErrors)
                } else {
                    setErrors({})
                }
            }
        }
        previousValues.current = values
    }, [values, errors, t])

    return <>{children}</>
}

interface StepProps {
    onNext: () => void
    onSuccessCallback: () => void
    onErrorCallback: () => void
}

const generateUniqueId = (prefix: string) => {
    return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`
}

export const TeamSetupForm: React.FC<StepProps> = ({ onNext, onSuccessCallback, onErrorCallback }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.InviteTeamMember]))
    const profile = useSelector(selectProfileDetails)

    const findDuplicate = (data: Member[]) => {
        const emailMap: { [key: string]: number[] } = {}
        const duplicateIndices: number[] = []

        data.forEach((entry, index) => {
            if (emailMap[entry.value.email]) {
                emailMap[entry.value.email].push(index)
            } else {
                if (Boolean(entry.value.email)) {
                    emailMap[entry.value.email] = [index]
                }
            }
        })

        for (const indices of Object.values(emailMap)) {
            if (indices.length > 1) {
                duplicateIndices.push(...indices)
            }
        }
        return duplicateIndices
    }

    return (
        <Formik
            initialValues={{
                members: [
                    { id: generateUniqueId('member'), value: { email: '', name: '' } },
                    { id: generateUniqueId('member'), value: { email: '', name: '' } },
                    { id: generateUniqueId('member'), value: { email: '', name: '' } },
                ],
            }}
            validationSchema={yup.object({
                members: yup
                    .array()
                    .of(
                        yup
                            .object({
                                id: yup.string().required(t('errorMessage.validation.requiredField')),
                                value: yup.object().test('value-validation', 'Invalid value', function (value) {
                                    if (value?.email || value.name) {
                                        if (!yup.string().email().isValidSync(value?.email))
                                            return this.createError({
                                                message: t('errorMessage.validation.enterValidEmail', {
                                                    email: 'name@example.com',
                                                }),
                                            })
                                        if (!value?.email) {
                                            return this.createError({
                                                message: t('errorMessage.validation.enterEmailAddress'),
                                            })
                                        }
                                        if (value?.email == profile.email) {
                                            return this.createError({
                                                message: t('errorMessage.validation.userAlreadyInTeamSubtext'),
                                            })
                                        }
                                        if (!value.name) {
                                            return this.createError({
                                                message: t('errorMessage.signUp.enterMemberName'),
                                            })
                                        }
                                    }
                                    return true
                                }),
                            })
                            .required(),
                    )
                    .test('at-least-one-member', t('errorMessage.validation.enterEmailAddress'), function (members) {
                        return members?.some((member: Member) => Object.values(member.value).every(val => val))
                    }),
            })}
            onSubmit={(values, action) => {
                if (isLoading) return
                const duplicateIndex = findDuplicate(values.members)
                if (duplicateIndex.length > 0) {
                    duplicateIndex.forEach((index: number) => {
                        action.setFieldError(
                            `members[${index}].value`,
                            t('errorMessage.validation.hasTheSameEmailIntheList'),
                        )
                    })
                } else {
                    const submitValues = values.members.filter(value => Boolean(value.value.email && value.value.name))
                    if (submitValues.length > 0) {
                        const payload = submitValues.map(value => {
                            return {
                                username: value.value.name,
                                email: value.value.email,
                            }
                        })
                        dispatch({
                            type: RegisterActions.MASS_INVITE,
                            payload: { data: payload, onSuccess: onSuccessCallback, onError: onErrorCallback },
                        })
                    }
                }
            }}
        >
            {({ setFieldValue, values, errors }) => {
                const addMember = () => {
                    const newMember: Member = {
                        id: generateUniqueId('member'),
                        value: { email: '', name: '' },
                    }
                    setFieldValue('members', [...values.members, newMember])
                }
                return (
                    <FormContainer>
                        <ErrorBoundary>
                            {values.members.map((member: Member, index: number) => {
                                const formErrors = !Array.isArray(errors.members) ? errors.members : undefined
                                const fieldErrors = Array.isArray(errors.members) ? errors.members : null
                                const currentFieldError = fieldErrors && (fieldErrors[index] as { value: string })
                                const errMsg = currentFieldError?.value || formErrors

                                return (
                                    <div key={member.id}>
                                        <RichInfoList
                                            value={member.value}
                                            valueKeys={['email', 'name']}
                                            isDraggable={false}
                                            mainInputData={{
                                                type: 'text',
                                                props: {
                                                    label: t('logIn.resetPassword.emailTitle'),
                                                    labelSize: 'l',
                                                },
                                            }}
                                            secondaryInputData={{
                                                type: 'text',
                                                props: {
                                                    label: t('accountDetails.personal.nameHeader'),
                                                    labelSize: 'l',
                                                },
                                            }}
                                            isDeletable={values.members.length > 3}
                                            onChange={value => {
                                                setFieldValue(`members[${index}].value`, value)
                                            }}
                                            onDelete={() => {
                                                const updatedMembers = values.members.filter((_, idx) => idx !== index)
                                                setFieldValue('members', updatedMembers)
                                            }}
                                            hasError={Boolean(errMsg)}
                                            error={errMsg}
                                        />
                                    </div>
                                )
                            })}
                        </ErrorBoundary>
                        <div>
                            <RectangleButton
                                form="outlined"
                                size="m"
                                label={t('button.addEmail')}
                                icon="plus"
                                type="button"
                                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                    e.preventDefault()
                                    addMember()
                                }}
                            />
                        </div>
                        <div className="buttons-group">
                            <RectangleButton
                                type="submit"
                                label={t('button.next')}
                                size="l"
                                isLoading={isLoading}
                                isDisabled={isLoading}
                            />
                            <RectangleButton
                                form="outlined"
                                type="button"
                                label={t('button.skip')}
                                size="l"
                                onClick={onNext}
                            />
                        </div>
                    </FormContainer>
                )
            }}
        </Formik>
    )
}
